import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, Workflow, FileSpreadsheet, Layers, Scale, GitBranch, Wand2,
  Fingerprint, ShieldCheck, BarChart3, Tag, Rocket, Coins, Gauge, Users,
  ArrowRight, ArrowDown, CheckCircle2, Mail, Server, Lock, Cpu, Heart,
  PlayCircle, Copy, Check, Sprout, Building2, Stethoscope, BookOpen,
  ShoppingBag, Briefcase, Palette, Landmark, Boxes, Clapperboard, Hammer,
} from "lucide-react";
import api from "../utils/api";
import { useTheme } from "../contexts/ThemeContext";

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmtPrice = (cents, cur) => {
  const sym = { eur: "€", usd: "$", gbp: "£" }[(cur || "").toLowerCase()] || "";
  return cents ? `${sym}${(cents / 100).toFixed(0)}` : "Free";
};

// ─── 1. Animated typing hero ─────────────────────────────────────────────────
const HERO_LINES = [
  "bank statements that match by Friday — promise.",
  "thousands of rows, reconciled while your tea cools.",
  "rules, not vibes. fuzzy matches, not guesses.",
  "no AI hallucinations. just maths, regex, and grace.",
];

function TypingHero() {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | pause | erasing
  useEffect(() => {
    const cur = HERO_LINES[idx];
    let t;
    if (phase === "typing") {
      if (text.length < cur.length) t = setTimeout(() => setText(cur.slice(0, text.length + 1)), 32);
      else t = setTimeout(() => setPhase("pause"), 1800);
    } else if (phase === "pause") {
      t = setTimeout(() => setPhase("erasing"), 400);
    } else {
      if (text.length > 0) t = setTimeout(() => setText(text.slice(0, -1)), 16);
      else { setPhase("typing"); setIdx((idx + 1) % HERO_LINES.length); }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx]);
  return (
    <span data-testid="hero-typing" style={{ color: "var(--sage)" }}>
      {text}
      <span style={{ display: "inline-block", width: "0.55ch", marginLeft: "2px", background: "var(--sage)", animation: "blink 1s steps(2) infinite", height: "1em", verticalAlign: "baseline" }} />
    </span>
  );
}

// ─── 2. Subtle animated background (floating shapes + grain) ─────────────────
function AnimatedBg() {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      {[
        { top: "10%", left: "5%", size: 280, delay: 0 },
        { top: "60%", left: "85%", size: 220, delay: 4 },
        { top: "35%", left: "70%", size: 160, delay: 2 },
        { top: "80%", left: "15%", size: 180, delay: 6 },
      ].map((b, i) => (
        <div key={i} style={{
          position: "absolute", top: b.top, left: b.left,
          width: b.size, height: b.size, borderRadius: "50%",
          background: `radial-gradient(circle, var(--sage-light) 0%, transparent 70%)`,
          opacity: 0.45,
          animation: `float 18s ease-in-out ${b.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

// ─── 3. Flippable feature card ───────────────────────────────────────────────
function FlipCard({ icon: Icon, title, body, joke, testid }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div data-testid={testid} onClick={() => setFlipped(!flipped)}
      style={{ perspective: 1200, cursor: "pointer", height: 210 }}>
      <div style={{
        position: "relative", width: "100%", height: "100%",
        transformStyle: "preserve-3d",
        transition: "transform 0.55s cubic-bezier(.4,.2,.2,1)",
        transform: flipped ? "rotateY(180deg)" : "rotateY(0)",
      }}>
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden",
          background: "white", border: "1px solid var(--border-default)",
          borderRadius: "14px", padding: "1.25rem", display: "flex",
          flexDirection: "column", gap: "0.6rem",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          transition: "border-color 0.2s, transform 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--sage)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-default)"; e.currentTarget.style.transform = "translateY(0)"; }}>
          <div style={{ width: 42, height: 42, borderRadius: "10px", background: "var(--sage-light)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--sage)" }}>
            <Icon size={22} strokeWidth={1.8} />
          </div>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, fontFamily: "Bricolage Grotesque, sans-serif" }}>{title}</h3>
          <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.45 }}>{body}</p>
          <p style={{ margin: "auto 0 0", fontSize: "0.68rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Tap for wisdom →</p>
        </div>
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden",
          transform: "rotateY(180deg)",
          background: "var(--sage)", color: "white",
          borderRadius: "14px", padding: "1.25rem", display: "flex",
          flexDirection: "column", justifyContent: "center", alignItems: "center",
          textAlign: "center", fontFamily: "Bricolage Grotesque, sans-serif",
        }}>
          <Sparkles size={18} style={{ opacity: 0.8, marginBottom: "0.5rem" }} />
          <p style={{ margin: 0, fontSize: "0.95rem", lineHeight: 1.5, fontWeight: 500 }}>{joke}</p>
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: Workflow, title: "Sequential rule engine", body: "Exact, tolerance, date, aggregate, fuzzy — stacked and run top to bottom. Like a recipe, but for accountants.", joke: "Yes, the rules run in order. No, they can't skip ahead just because rule 3 is taking a nap." },
  { icon: FileSpreadsheet, title: "Excel & CSV that 'just works'", body: "Messy headers, banner rows, blank columns — skip-rows handles it. Don't clean the file; clean the rules.", joke: "That row 1 that says 'Generated on 14th April 2026'? We ignore it too. Like a bad ex." },
  { icon: Layers, title: "Transform sets", body: "Trim, regex, lookup, cleanup, arithmetic, conditionals — as reusable sets, not one-off hacks.", joke: "Think of it as LEGO for dirty data. Except the instructions actually make sense." },
  { icon: Scale, title: "Numeric & date tolerance", body: "Match within ±0.01 or ±3 days. Because real-world data was rounded by a human in a hurry.", joke: "We forgive 0.001 cent. We do not forgive 3 cents. Know the difference." },
  { icon: GitBranch, title: "Hub-and-spoke (multi-file) recon", body: "One anchor vs N target files. Each leg has its own rule set. Conflicts bubble up for review.", joke: "Imagine having 8 bank statements to reconcile against 1 ledger. Now imagine sleeping again." },
  { icon: Wand2, title: "Fuzzy text matching", body: "RapidFuzz-powered similarity with threshold. Runs last, lands in review — human in the loop.", joke: "'BANK OF AMERICA' ≈ 'bank of amer.' at 94%. Computers finally understand typos." },
  { icon: Fingerprint, title: "Conditional & nested rules", body: "if amount > 10k then regex extract invoice id else lookup vendor. As deep as you dare.", joke: "It's like writing Excel formulas, except they don't break when someone sneezes on row 847." },
  { icon: BarChart3, title: "Dynamic reporting", body: "Slice by any column bucket, debit/credit split, per-rule contribution, Excel multi-sheet export.", joke: "Your CFO will nod slowly. That's the highest compliment their species offers." },
  { icon: Tag, title: "Manual match & dismiss", body: "Click rows from both sides, set a reason, done. Or flag as 'one-sided acknowledged' and move on.", joke: "Sometimes reconciliation is emotional. We built a button for that." },
  { icon: Users, title: "Multi-tenant & RBAC", body: "Superadmin → dept admin → dept user. Rule sets, lookups and runs are dept-scoped end-to-end.", joke: "Finance sees finance. Ops sees ops. Nobody sees Karen's expense report. Everyone wins." },
  { icon: ShieldCheck, title: "Self-hosted, air-gap friendly", body: "Runs on your box. Your VPC, your MongoDB, your files. No data tourism.", joke: "The only cloud we need is the one blocking our meeting view on a Friday afternoon." },
  { icon: Rocket, title: "One-click rerun", body: "Same files, same rules, updated lookup — rerun in a click. Full lineage preserved.", joke: "Because 'just run it again' is the most honest instruction in finance." },
  { icon: Coins, title: "Aggregate (1:N) matching", body: "Group on one side, compare the sum/avg to a single value on the other. With tolerance.", joke: "One invoice, seventeen tiny payments. We see you, B2B workflow. We see you." },
  { icon: Gauge, title: "Templates, imports & exports", body: "Save rule sets, transform sets, file configs as JSON. Move between environments in minutes.", joke: "Promotes from dev to prod without HR involvement." },
  { icon: Sparkles, title: "Rule-level enable/disable", body: "Park a rule without deleting it. Toggle on when the regulator asks nicely.", joke: "Rules on sabbatical. Like you, but more organised." },
];

// ─── 4. Painpoint vertical tabs ──────────────────────────────────────────────
const DOMAINS = [
  {
    key: "finance", label: "Finance & Accounting", icon: Landmark,
    pain: "Month-end close chaos — bank statements vs GL, intercompany eliminations, credit-card feeds, all reconciled in Excel formulas someone named Dave wrote in 2017.",
    cure: "Define your match rules once (exact key + amount tolerance + date window). Upload the two files. Fuzzy-match the long-tail of weird descriptions. Close in hours, not weekends.",
  },
  {
    key: "freelancing", label: "Freelancing & Solopreneurs", icon: Palette,
    pain: "Stripe payouts, PayPal deposits, Wise transfers, UPI receipts — five dashboards, one client list, zero clarity on who actually paid and who's ghosting the invoice.",
    cure: "One rule set per payment channel. Reconcile them against your invoice log. The 'unmatched_a' tab is now your chase list. The 'partial' tab is the polite reminders.",
  },
  {
    key: "ecommerce", label: "E-commerce & D2C", icon: ShoppingBag,
    pain: "Marketplace settlements (Shopify, Amazon, Flipkart) vs bank deposits. Fees, holds, refunds, commissions, weird FX rounding. Daily sanity check turns into weekly headache.",
    cure: "Aggregate rule: sum of N order rows = 1 settlement deposit (± tolerance). Nested conditional for refund categorisation. Leftover variance goes to the finance team with reasons attached.",
  },
  {
    key: "healthcare", label: "Healthcare & Clinics", icon: Stethoscope,
    pain: "Insurance claim submissions vs actual disbursements. EOBs (explanation of benefits) come 30 days late with adjustments, write-offs and partial approvals. Manual = error-prone.",
    cure: "Match claim id exactly + amount tolerance + date window. Fuzzy rule catches insurer typos in patient names. Partial tab lights up every write-off for review.",
  },
  {
    key: "education", label: "Education & EdTech", icon: BookOpen,
    pain: "Fee collection across payment gateways, scholarship disbursements, vendor payments for hostels/canteens. Every term-end, the bursar's office rebuilds the same Excel tracker from scratch.",
    cure: "One rule set per fee category. Aggregate against student ledger. Automated exception flags for scholarship mismatches and refund cases.",
  },
  {
    key: "nonprofit", label: "Nonprofits & NGOs", icon: Heart,
    pain: "Grant tranche receipts vs project budget drawdowns. Donor-restricted funds need traceability. Auditors ask questions that require spreadsheet archeology.",
    cure: "Rule set per grantor, per restriction class. Full lineage via one-click rerun means you can prove every allocation. Audit exports in one click.",
  },
  {
    key: "manufacturing", label: "Manufacturing & Supply Chain", icon: Boxes,
    pain: "Goods-receipt-notes vs supplier invoices vs purchase orders. 3-way match in an ERP is a licence you already paid for but nobody understands how to configure.",
    cure: "Hub-and-spoke: PO as anchor, GRN and invoice as spokes. Aggregate for partial shipments. Date tolerance for 'received today, invoiced yesterday' cases. Done by lunch.",
  },
  {
    key: "saas", label: "SaaS & Subscriptions", icon: Cpu,
    pain: "Stripe payouts, refunds, disputes, tax withholdings, multi-currency settlements vs your MRR reporting. Finance and product are quietly disagreeing on what 'revenue' means.",
    cure: "One rule set per payment processor. Aggregate charges per day, tolerance for FX slippage. Disputes/refunds caught as 'partial' for review before they hit the wrong period.",
  },
  {
    key: "legal", label: "Legal & Professional Services", icon: Briefcase,
    pain: "Client trust accounts, retainer draws, expense advances, disbursements — every state bar has a rule, every partner has an opinion, every reconciliation is a Friday late-night.",
    cure: "Rule sets scoped per matter. Aggregate sub-ledger vs trust balance. Strict date tolerance = 0 for compliance. Audit export signed and filed by 5pm.",
  },
  {
    key: "construction", label: "Construction & Real Estate", icon: Hammer,
    pain: "Progress invoices, retention holdbacks, subcontractor payments, site-level P&L. Every project is a one-off reconciliation built in a fresh Excel tab.",
    cure: "Transform sets normalise invoice formats. Aggregate per sub, per project code. Retention holdbacks caught via conditional rules. Projects close on time, for once.",
  },
  {
    key: "media", label: "Media & Content Creators", icon: Clapperboard,
    pain: "Ad revenue (YouTube, Twitch, Spotify), brand deals, affiliate payouts, Patreon — reconcile platform reports vs bank deposits while figuring out what's 'earnings' vs 'ad credits'.",
    cure: "One rule set per platform. Fuzzy match on creator/campaign names. Aggregate daily earnings vs single monthly deposit with tolerance. Tax filing becomes a boring 2-hour task.",
  },
];

function PainpointTabs() {
  const [active, setActive] = useState("finance");
  const cur = DOMAINS.find(d => d.key === active) || DOMAINS[0];
  const Icon = cur.icon;
  return (
    <div data-testid="pain-tabs" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: "1.5rem", marginTop: "2rem" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", borderRight: "1px solid var(--border-default)", paddingRight: "0.5rem" }}>
        {DOMAINS.map(d => {
          const I = d.icon;
          const on = d.key === active;
          return (
            <button
              key={d.key}
              data-testid={`pain-tab-${d.key}`}
              onClick={() => setActive(d.key)}
              style={{
                display: "flex", alignItems: "center", gap: "0.6rem",
                padding: "0.65rem 0.85rem",
                background: on ? "var(--sage-light)" : "transparent",
                color: on ? "var(--sage)" : "var(--text-secondary)",
                border: "none", borderRadius: "0.5rem",
                cursor: "pointer", textAlign: "left", fontWeight: on ? 600 : 500,
                fontSize: "0.88rem", fontFamily: "DM Sans, sans-serif",
                borderLeft: on ? "3px solid var(--sage)" : "3px solid transparent",
              }}>
              <I size={15} /> {d.label}
            </button>
          );
        })}
      </div>
      <div key={cur.key} style={{ padding: "0.5rem 0.25rem", animation: "fadein 0.35s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.75rem" }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--sage-light)", color: "var(--sage)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={18} />
          </div>
          <h3 style={{ margin: 0, fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "1.25rem", fontWeight: 700 }}>{cur.label}</h3>
        </div>
        <div style={{ background: "#FEF3C7", border: "1px dashed #F59E0B", borderRadius: 10, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
          <p style={{ margin: 0, fontSize: "0.72rem", color: "#92400E", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.35rem" }}>The painpoint</p>
          <p style={{ margin: 0, fontSize: "0.93rem", color: "#78350F", lineHeight: 1.55 }}>{cur.pain}</p>
        </div>
        <div style={{ background: "var(--sage-light)", border: "1px solid var(--sage-border)", borderRadius: 10, padding: "1rem 1.1rem" }}>
          <p style={{ margin: 0, fontSize: "0.72rem", color: "var(--sage)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.35rem" }}>How ReconX fixes it</p>
          <p style={{ margin: 0, fontSize: "0.93rem", color: "var(--text-primary)", lineHeight: 1.55 }}>{cur.cure}</p>
        </div>
      </div>
    </div>
  );
}

// ─── 5. Animated reconciliation steps ────────────────────────────────────────
const PROCESS_STEPS = [
  { label: "Upload", icon: FileSpreadsheet, desc: "Drop in your two (or ten) files. Excel, CSV, banner rows and all." },
  { label: "Transform", icon: Layers, desc: "Clean columns, apply lookups, extract regex, enrich with context." },
  { label: "Rules", icon: Workflow, desc: "Match exactly, then with tolerance, then fuzzy. Stacked in sequence." },
  { label: "Review", icon: CheckCircle2, desc: "Manual match the leftovers. One-sided dismiss the orphans." },
  { label: "Report", icon: BarChart3, desc: "Slice, export, share. Period closed. Now go outside." },
];

function ProcessAnimation() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % PROCESS_STEPS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="process-anim" style={{ display: "flex", gap: "0.5rem", alignItems: "stretch", flexWrap: "wrap", justifyContent: "center", marginTop: "2rem" }}>
      {PROCESS_STEPS.map((s, i) => {
        const I = s.icon;
        const active = i === step;
        const done = i < step;
        return (
          <React.Fragment key={s.label}>
            <div style={{
              flex: "1 1 160px", minWidth: 160, padding: "1.1rem 0.9rem",
              background: active ? "var(--sage)" : done ? "var(--sage-light)" : "white",
              color: active ? "white" : done ? "var(--sage)" : "var(--text-secondary)",
              border: `1px solid ${active ? "var(--sage)" : "var(--border-default)"}`,
              borderRadius: 12,
              transition: "all 0.45s cubic-bezier(.4,.2,.2,1)",
              transform: active ? "scale(1.04)" : "scale(1)",
              boxShadow: active ? "0 8px 24px rgba(0,0,0,0.08)" : "none",
              textAlign: "center",
            }}>
              <I size={22} style={{ marginBottom: "0.4rem" }} />
              <div style={{ fontWeight: 700, fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "0.95rem" }}>{i + 1}. {s.label}</div>
              <div style={{ fontSize: "0.76rem", marginTop: "0.25rem", opacity: active || done ? 1 : 0.75, lineHeight: 1.4 }}>{s.desc}</div>
            </div>
            {i < PROCESS_STEPS.length - 1 && (
              <div style={{ display: "flex", alignItems: "center", color: "var(--text-muted)" }}>
                <ArrowRight size={16} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── 5b. Reconciliation MODES — Single / Multi / Chain with mini data flow ───
const MODE_TABS = [
  {
    key: "single",
    label: "Single-file",
    blurb: "Two files, one rule set. The classic 1:1 recon.",
    color: "var(--sage)",
    files: [
      { name: "bank.csv",   rows: [{ ref: "TXN-101", amt: 1200 }, { ref: "TXN-102", amt:  450 }, { ref: "TXN-103", amt:  850 }] },
      { name: "ledger.csv", rows: [{ ref: "TXN-101", amt: 1200 }, { ref: "TXN-102", amt:  450 }, { ref: "TXN-999", amt: 300 }] },
    ],
    result: [
      { badge: "✓ matched",   left: "TXN-101 · 1200", right: "TXN-101 · 1200", rule: "exact on ref" },
      { badge: "✓ matched",   left: "TXN-102 · 450",  right: "TXN-102 · 450",  rule: "exact on ref" },
      { badge: "unmatched",   left: "TXN-103 · 850",  right: "—",               rule: "no match on ledger" },
      { badge: "unmatched",   left: "—",              right: "TXN-999 · 300",  rule: "no match on bank" },
    ],
  },
  {
    key: "multi",
    label: "Multi-file (hub-and-spoke)",
    blurb: "One anchor, many targets. Try bank first, then card, then wallet.",
    color: "#1E40AF",
    files: [
      { name: "ledger (anchor)", rows: [{ ref: "L-01" }, { ref: "L-02" }, { ref: "L-03" }, { ref: "L-04" }] },
      { name: "bank.csv",        rows: [{ ref: "L-01" }, { ref: "L-03" }] },
      { name: "card.csv",        rows: [{ ref: "L-02" }] },
    ],
    result: [
      { badge: "✓ leg 1",  left: "L-01", right: "bank",  rule: "matched on hop 1" },
      { badge: "✓ leg 1",  left: "L-03", right: "bank",  rule: "matched on hop 1" },
      { badge: "✓ leg 2",  left: "L-02", right: "card",  rule: "matched on hop 2 (cascade)" },
      { badge: "unmatched",left: "L-04", right: "—",     rule: "still open after all legs" },
    ],
  },
  {
    key: "chain",
    label: "Chain (relay)",
    blurb: "Walk files in sequence. PO → Invoice → Payment — the join key changes every hop.",
    color: "#8B5CF6",
    files: [
      { name: "POs.csv",      rows: [{ po: "P-01", amt: 1000 }, { po: "P-02", amt: 500 }] },
      { name: "Invoices.csv", rows: [{ inv: "I-77", po_ref: "P-01", amt: 1000 }, { inv: "I-78", po_ref: "P-02", amt: 500 }] },
      { name: "Payments.csv", rows: [{ pay: "Y-21", inv_ref: "I-77", amt: 1000 }] },
    ],
    result: [
      { badge: "✓ complete",        left: "P-01", right: "I-77 → Y-21", rule: "hop 1: po=po_ref · hop 2: inv=inv_ref" },
      { badge: "broken at hop 2",   left: "P-02", right: "I-78 → —",     rule: "invoice found, no payment yet" },
    ],
  },
];

function ModeDataSnippet({ file, color }) {
  const keys = file.rows.length ? Object.keys(file.rows[0]) : [];
  return (
    <div style={{ background: "white", border: "1px solid var(--border-default)", borderRadius: 10, overflow: "hidden", fontFamily: "JetBrains Mono, monospace", fontSize: "0.72rem" }}>
      <div style={{ padding: "0.35rem 0.6rem", background: "var(--bg-subtle)", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
        <span style={{ fontWeight: 700, fontSize: "0.7rem" }}>{file.name}</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#fafafa" }}>
            {keys.map(k => <th key={k} style={{ padding: "0.25rem 0.5rem", textAlign: "left", color: "var(--text-muted)", fontWeight: 600, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{k}</th>)}
          </tr>
        </thead>
        <tbody>
          {file.rows.map((r, i) => (
            <tr key={i} style={{ borderTop: "1px solid #f3f4f6" }}>
              {keys.map(k => <td key={k} style={{ padding: "0.25rem 0.5rem", whiteSpace: "nowrap" }}>{String(r[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModesAnimation() {
  const [tab, setTab] = useState("single");
  const [highlighted, setHighlighted] = useState(0);
  const active = MODE_TABS.find(t => t.key === tab) || MODE_TABS[0];

  // Auto-advance through the result rows to animate reconciliation
  useEffect(() => {
    setHighlighted(0);
    const t = setInterval(() => {
      setHighlighted(h => (h + 1) % (active.result.length + 1));
    }, 1100);
    return () => clearInterval(t);
  }, [tab, active.result.length]);

  return (
    <div data-testid="modes-animation" style={{ marginTop: "1.5rem" }}>
      {/* Tab bar */}
      <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        {MODE_TABS.map(m => (
          <button key={m.key}
            data-testid={`mode-tab-${m.key}`}
            onClick={() => setTab(m.key)}
            style={{
              padding: "0.55rem 1.1rem", borderRadius: 10, cursor: "pointer",
              border: `1px solid ${tab === m.key ? m.color : "var(--border-default)"}`,
              background: tab === m.key ? m.color : "white",
              color: tab === m.key ? "white" : "var(--text-secondary)",
              fontWeight: 600, fontSize: "0.88rem",
              transition: "all 0.3s ease",
              boxShadow: tab === m.key ? "0 6px 20px rgba(0,0,0,0.08)" : "none",
            }}>
            {m.label}
          </button>
        ))}
      </div>

      <div data-testid={`mode-panel-${active.key}`} style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-default)", borderRadius: 16, padding: "1.5rem", maxWidth: 1050, margin: "0 auto" }}>
        <p style={{ textAlign: "center", fontSize: "0.88rem", color: "var(--text-secondary)", margin: "0 0 1.25rem" }}>
          <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: active.color, marginRight: "0.5rem" }} />
          {active.blurb}
        </p>

        {/* Files row */}
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${active.files.length}, 1fr)`, gap: "0.75rem", marginBottom: "1rem" }}>
          {active.files.map((f, i) => (
            <React.Fragment key={f.name}>
              <ModeDataSnippet file={f} color={active.color} />
            </React.Fragment>
          ))}
        </div>

        <div style={{ textAlign: "center", margin: "0.5rem 0 0.75rem" }}>
          <ArrowRight size={18} style={{ color: active.color, transform: "rotate(90deg)" }} />
        </div>

        {/* Result rows — animate one-by-one */}
        <div style={{ background: "white", border: "1px solid var(--border-default)", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "0.5rem 0.75rem", background: active.color, color: "white", fontWeight: 700, fontSize: "0.8rem", fontFamily: "Bricolage Grotesque, sans-serif" }}>
            Reconciled result
          </div>
          {active.result.map((r, i) => {
            const isActive = i === highlighted % active.result.length;
            const isMatched = r.badge.startsWith("✓");
            return (
              <div key={i} style={{
                padding: "0.5rem 0.75rem",
                borderTop: i > 0 ? "1px solid #f3f4f6" : "none",
                display: "grid", gridTemplateColumns: "120px 1fr 1fr 1.2fr", gap: "0.75rem", alignItems: "center",
                fontFamily: "JetBrains Mono, monospace", fontSize: "0.74rem",
                background: isActive ? (isMatched ? "rgba(22,163,74,0.08)" : "rgba(220,38,38,0.06)") : "white",
                transition: "background 0.4s ease",
                opacity: isActive ? 1 : 0.75,
              }}>
                <span style={{ padding: "0.15rem 0.5rem", borderRadius: 6,
                               background: isMatched ? "#dcfce7" : r.badge.includes("broken") ? "#fee2e2" : "#f3f4f6",
                               color:      isMatched ? "#166534" : r.badge.includes("broken") ? "#991b1b" : "#6b7280",
                               fontSize: "0.66rem", fontWeight: 700, textAlign: "center" }}>
                  {r.badge}
                </span>
                <span>{r.left}</span>
                <span>{r.right}</span>
                <span style={{ color: "var(--text-muted)", fontSize: "0.7rem", fontStyle: "italic" }}>{r.rule}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── 6. Pricing cards (server-driven) ────────────────────────────────────────
function PricingSection({ plans, stripeEnabled, onBuy }) {
  if (!plans?.length) return null;
  return (
    <div id="pricing" data-testid="pricing-section" style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, 1fr)`, gap: "1.25rem", marginTop: "2rem" }}>
      {plans.map(p => {
        const price = fmtPrice(p.price_cents, p.currency);
        const suffix = p.price_cents ? (p.interval === "year" ? "/year" : p.interval === "month" ? "/mo" : "") : (p.trial_days ? `${p.trial_days}-day trial` : "");
        return (
          <div key={p.id} data-testid={`plan-card-${p.id}`} style={{
            position: "relative",
            background: "white",
            border: `1px solid ${p.highlight ? "var(--sage)" : "var(--border-default)"}`,
            borderRadius: 16,
            padding: "1.5rem",
            boxShadow: p.highlight ? "0 10px 30px rgba(0,0,0,0.06)" : "0 1px 2px rgba(0,0,0,0.03)",
            transform: p.highlight ? "translateY(-8px)" : "none",
          }}>
            {p.highlight && (
              <span style={{ position: "absolute", top: 12, right: 12, background: "var(--sage)", color: "white", fontSize: "0.65rem", padding: "0.2rem 0.55rem", borderRadius: 999, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700 }}>Popular</span>
            )}
            <h3 style={{ margin: 0, fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "1.2rem", fontWeight: 700 }}>{p.name}</h3>
            <div style={{ margin: "0.75rem 0 1rem", display: "flex", alignItems: "baseline", gap: "0.35rem" }}>
              <span style={{ fontSize: "2.2rem", fontWeight: 800, fontFamily: "Bricolage Grotesque, sans-serif", color: "var(--sage)" }}>{price}</span>
              <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{suffix}</span>
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1.25rem" }}>
              {(p.features || []).map((f, i) => (
                <li key={i} style={{ display: "flex", gap: "0.5rem", fontSize: "0.85rem", color: "var(--text-secondary)", alignItems: "flex-start" }}>
                  <CheckCircle2 size={15} style={{ color: "var(--sage)", flexShrink: 0, marginTop: 2 }} />
                  {f}
                </li>
              ))}
            </ul>
            <button data-testid={`buy-btn-${p.id}`} onClick={() => onBuy(p)}
              className="btn-primary"
              style={{ width: "100%", padding: "0.7rem", fontSize: "0.9rem", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}>
              {p.kind === "free" ? "Start free trial" : (stripeEnabled ? "Buy now" : "Request access")} <ArrowRight size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── 7. Buy / capture modal ─────────────────────────────────────────────────
function BuyModal({ plan, stripeEnabled, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const wantsStripe = stripeEnabled && plan.kind !== "free" && plan.price_cents > 0;

  const submit = async () => {
    if (!form.name || !form.email) { setError("Name and email are required."); return; }
    setBusy(true); setError("");
    try {
      if (wantsStripe) {
        const res = await api.post("/billing/checkout", {
          plan_id: plan.id,
          origin_url: window.location.origin,
          contact_name: form.name,
          contact_email: form.email,
          contact_company: form.company,
        });
        window.location.href = res.data.url;
      } else {
        const res = await api.post("/billing/capture", { plan_id: plan.id, ...form });
        setDone(res.data.message);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="buy-modal" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,15,15,0.55)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 14, padding: "1.5rem", maxWidth: 460, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        {done ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 52, height: 52, margin: "0 auto 0.75rem", background: "var(--sage-light)", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--sage)" }}>
              <Check size={26} />
            </div>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", margin: "0 0 0.5rem" }}>You're on the list</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>{done}</p>
            <button className="btn-primary" onClick={onClose} data-testid="buy-done-btn" style={{ padding: "0.55rem 1.5rem" }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
              <div>
                <h3 style={{ margin: 0, fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "1.15rem", fontWeight: 700 }}>{plan.name}</h3>
                <p style={{ margin: "0.15rem 0 0", fontSize: "0.8rem", color: "var(--text-muted)" }}>{fmtPrice(plan.price_cents, plan.currency)}{plan.price_cents ? ` / ${plan.interval}` : (plan.trial_days ? ` · ${plan.trial_days}-day trial` : "")}</p>
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "1.5rem", lineHeight: 1 }}>×</button>
            </div>
            {!wantsStripe && (
              <div style={{ background: "var(--sage-light)", border: "1px solid var(--sage-border)", padding: "0.6rem 0.8rem", borderRadius: 8, fontSize: "0.78rem", color: "var(--text-primary)", marginBottom: "0.9rem", lineHeight: 1.5 }}>
                {plan.kind === "free"
                  ? "Leave your details — we'll set up your trial account and email you once it's ready. Usually within a week."
                  : "We'll email you a secure payment link and activate your account right after the payment lands. We get back within a week."}
              </div>
            )}
            <div style={{ display: "grid", gap: "0.55rem" }}>
              <input data-testid="buy-name" className="recon-input" placeholder="Your name *" value={form.name} onChange={e => f("name", e.target.value)} />
              <input data-testid="buy-email" type="email" className="recon-input" placeholder="Work email *" value={form.email} onChange={e => f("email", e.target.value)} />
              <input data-testid="buy-company" className="recon-input" placeholder="Company (optional)" value={form.company} onChange={e => f("company", e.target.value)} />
              {!wantsStripe && (
                <textarea data-testid="buy-message" className="recon-input" rows={3} placeholder="Anything we should know? (optional)" value={form.message} onChange={e => f("message", e.target.value)} style={{ resize: "vertical" }} />
              )}
            </div>
            {error && <p data-testid="buy-error" style={{ color: "#B91C1C", fontSize: "0.78rem", margin: "0.65rem 0 0" }}>{error}</p>}
            <button data-testid="buy-submit" onClick={submit} disabled={busy} className="btn-primary"
              style={{ width: "100%", marginTop: "1rem", padding: "0.65rem", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}>
              {busy ? "Sending…" : (wantsStripe ? "Continue to payment" : "Send request")} <ArrowRight size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── 8. Core principles & ethics section ────────────────────────────────────
const PRINCIPLES = [
  { icon: Server, title: "Self-hosted by default", body: "Install on your own box, VPC, or datacenter. No mandatory cloud. No tenant-of-a-tenant shenanigans. Your infra, your rules." },
  { icon: Lock, title: "Data stays on your intranet", body: "Files, matches, rule sets, audit logs — they all live in your MongoDB. We don't phone home. You don't have to explain anything to compliance." },
  { icon: ShieldCheck, title: "No LLMs, no guessing", body: "Every match is explainable: rule name, rule type, mismatch reason. Deterministic logic only. Regulators nod approvingly." },
  { icon: Sprout, title: "Open formats, no lock-in", body: "Excel in. Excel out. JSON import/export for every config. Migrate to us in a weekend. Migrate away in a weekend too, no hard feelings." },
  { icon: Heart, title: "Humane defaults", body: "Sensible free trial. No dark patterns. No 'book a demo to see pricing'. We're not that kind of software." },
  { icon: Cpu, title: "Made for the long run", body: "Python, FastAPI, MongoDB, React. Boring, reliable, hireable. No vendor-locked frameworks that vanish in two years." },
];

// ─── 9. Landing page ─────────────────────────────────────────────────────────
export default function LandingPage() {
  const { settings } = useTheme();
  const [plans, setPlans] = useState([]);
  const [buyPlan, setBuyPlan] = useState(null);
  const nav = useNavigate();
  const appName = settings?.app_name || "ReconX";
  const tagline = settings?.app_tagline || "Reconciliation that finishes on a Friday.";
  const support = settings?.support_email || "hello@krri.sh";
  const stripeEnabled = !!settings?.stripe_enabled;

  useEffect(() => {
    api.get("/public/plans").then(r => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  return (
    <div data-testid="landing-page" style={{ minHeight: "100vh", background: "var(--bg-default)", color: "var(--text-primary)", fontFamily: "DM Sans, sans-serif" }}>
      <style>{`
        @keyframes blink { 50% { opacity: 0 } }
        @keyframes float { 0%,100%{ transform: translate(0,0) } 50%{ transform: translate(30px,-28px) } }
        @keyframes fadein { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>

      {/* Nav */}
      <header style={{ position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(10px)", background: "rgba(245,244,239,0.85)", borderBottom: "1px solid var(--border-default)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.9rem 1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: "var(--sage)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontFamily: "Bricolage Grotesque, sans-serif" }}>{appName[0]}</div>
            <span style={{ fontWeight: 700, fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "1.1rem" }}>{appName}</span>
          </div>
          <nav style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
            <a href="#features" style={{ color: "var(--text-secondary)", textDecoration: "none", fontSize: "0.88rem" }}>Features</a>
            <a href="#painpoints" style={{ color: "var(--text-secondary)", textDecoration: "none", fontSize: "0.88rem" }}>Who it's for</a>
            <a href="#process" style={{ color: "var(--text-secondary)", textDecoration: "none", fontSize: "0.88rem" }}>How it works</a>
            <Link data-testid="nav-kb" to="/knowledge-base" style={{ color: "var(--text-secondary)", textDecoration: "none", fontSize: "0.88rem" }}>Docs</Link>
            <a href="#pricing" style={{ color: "var(--text-secondary)", textDecoration: "none", fontSize: "0.88rem" }}>Pricing</a>
            <Link data-testid="nav-login" to="/login" className="btn-primary" style={{ padding: "0.4rem 1rem", fontSize: "0.85rem", textDecoration: "none" }}>Log in</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section style={{ position: "relative", padding: "5rem 1.5rem 4rem", overflow: "hidden" }}>
        <AnimatedBg />
        <div style={{ maxWidth: 980, margin: "0 auto", position: "relative", zIndex: 1, textAlign: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", padding: "0.3rem 0.75rem", background: "white", border: "1px solid var(--sage-border)", color: "var(--sage)", borderRadius: 999, fontSize: "0.75rem", fontWeight: 600, marginBottom: "1.25rem" }}>
            <Sparkles size={12} /> Rule-based · self-hosted · zero LLMs
          </span>
          <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 800, fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 1.08, margin: 0, letterSpacing: "-0.015em" }}>
            <span style={{ display: "block", color: "var(--text-primary)" }}>Reconcile anything.</span>
            <span style={{ display: "block", minHeight: "1.2em" }}><TypingHero /></span>
          </h1>
          <p style={{ maxWidth: 680, margin: "1.5rem auto 0", color: "var(--text-secondary)", fontSize: "1.08rem", lineHeight: 1.55 }}>
            Upload two (or ten) Excel files. Stack match rules like LEGO. Catch the long-tail with fuzzy matching. Ship the close. {appName} is a rule-based engine you install on your own servers — no AI, no guessing, no leaked data.
          </p>
          <div style={{ marginTop: "2rem", display: "flex", gap: "0.6rem", justifyContent: "center", flexWrap: "wrap" }}>
            <a data-testid="hero-pricing-btn" href="#pricing" className="btn-primary" style={{ padding: "0.7rem 1.4rem", fontSize: "0.95rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.45rem" }}>
              See pricing <ArrowDown size={15} />
            </a>
            <a data-testid="hero-process-btn" href="#process" className="btn-secondary" style={{ padding: "0.7rem 1.4rem", fontSize: "0.95rem", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.45rem" }}>
              <PlayCircle size={15} /> How it works
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ padding: "5rem 1.5rem", background: "white", borderTop: "1px solid var(--border-default)", borderBottom: "1px solid var(--border-default)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
            <p style={{ color: "var(--sage)", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Every tool you thought you needed in Excel</p>
            <h2 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", margin: "0.5rem 0 0.6rem", fontWeight: 700, letterSpacing: "-0.01em" }}>Features, laid out honestly.</h2>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.92rem" }}>Tap any card for a wildly unprofessional take.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
            {FEATURES.map((f, i) => <FlipCard key={i} {...f} testid={`feature-card-${i}`} />)}
          </div>
        </div>
      </section>

      {/* Painpoints */}
      <section id="painpoints" style={{ padding: "5rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <p style={{ color: "var(--sage)", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Ten ways this helps your team</p>
            <h2 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", margin: "0.5rem 0 0.6rem", fontWeight: 700, letterSpacing: "-0.01em" }}>Who it's for.</h2>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.92rem" }}>Pick the domain closest to your mess.</p>
          </div>
          <PainpointTabs />
        </div>
      </section>

      {/* Core principles / ethics */}
      <section id="principles" style={{ padding: "5rem 1.5rem", background: "white", borderTop: "1px solid var(--border-default)", borderBottom: "1px solid var(--border-default)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
            <p style={{ color: "var(--sage)", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>What we stand for</p>
            <h2 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", margin: "0.5rem 0 0.6rem", fontWeight: 700, letterSpacing: "-0.01em" }}>Core principles & ethics.</h2>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.92rem", maxWidth: 600, marginInline: "auto", lineHeight: 1.55 }}>
              We built this for teams who take their data seriously — auditors, regulators, finance leads — but got tired of software that takes itself too seriously.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
            {PRINCIPLES.map((p, i) => {
              const I = p.icon;
              return (
                <div key={i} data-testid={`principle-${i}`} style={{ padding: "1.2rem", background: "var(--bg-subtle)", border: "1px solid var(--border-default)", borderRadius: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--sage-light)", color: "var(--sage)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "0.75rem" }}>
                    <I size={18} />
                  </div>
                  <h3 style={{ margin: "0 0 0.35rem", fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "1.02rem", fontWeight: 700 }}>{p.title}</h3>
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.55 }}>{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process animation */}
      <section id="process" style={{ padding: "5rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <p style={{ color: "var(--sage)", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Reconciliation in 5 beats</p>
            <h2 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", margin: "0.5rem 0 0.6rem", fontWeight: 700, letterSpacing: "-0.01em" }}>How it works.</h2>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.92rem" }}>The whole flow, animated. Stare at it and feel the calm.</p>
          </div>
          <ProcessAnimation />

          <div style={{ textAlign: "center", marginTop: "3rem", marginBottom: "0.5rem" }}>
            <p style={{ color: "var(--sage)", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Three ways to reconcile</p>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "clamp(1.3rem, 2.5vw, 1.9rem)", margin: "0.4rem 0 0.3rem", fontWeight: 700 }}>Single, Multi, or Chained.</h3>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.88rem" }}>Pick the one that matches how your data actually flows.</p>
          </div>
          <ModesAnimation />
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: "5rem 1.5rem", background: "white", borderTop: "1px solid var(--border-default)", borderBottom: "1px solid var(--border-default)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <p style={{ color: "var(--sage)", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Simple plans</p>
            <h2 id="pricing-heading" style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", margin: "0.5rem 0 0.6rem", fontWeight: 700, letterSpacing: "-0.01em" }}>Pricing.</h2>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.92rem" }}>
              {stripeEnabled ? "Pick a plan. Stripe Checkout takes over from here." : "Pick a plan and we'll reach out within a week with a payment link."}
            </p>
          </div>
          <PricingSection plans={plans} stripeEnabled={stripeEnabled} onBuy={setBuyPlan} />
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "5rem 1.5rem" }}>
        <div style={{ maxWidth: 780, margin: "0 auto", textAlign: "center", padding: "2.5rem 1.5rem", background: "var(--sage)", borderRadius: 20, color: "white", position: "relative", overflow: "hidden" }}>
          <div aria-hidden style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.12), transparent 40%)", pointerEvents: "none" }} />
          <Mail size={30} style={{ marginBottom: "0.75rem", position: "relative" }} />
          <h2 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.2rem)", margin: 0, fontWeight: 700, position: "relative" }}>Let's talk.</h2>
          <p style={{ fontSize: "1rem", opacity: 0.92, margin: "0.75rem auto 1.5rem", maxWidth: 520, lineHeight: 1.55, position: "relative" }}>
            Got a weird reconciliation you can't crack? Want a self-hosted install on your box? Drop a line — we read every email and reply in under a week.
          </p>
          <a data-testid="cta-email" href={`mailto:${support}?subject=Tell me more about ${appName}`} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "white", color: "var(--sage)", padding: "0.75rem 1.5rem", borderRadius: 10, textDecoration: "none", fontWeight: 700, fontSize: "1rem" }}>
            {support} <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: "2rem 1.5rem", borderTop: "1px solid var(--border-default)", textAlign: "center", color: "var(--text-muted)", fontSize: "0.82rem" }}>
        © {new Date().getFullYear()} {appName}. Reconcile responsibly. · <a href={`mailto:${support}`} style={{ color: "var(--sage)", textDecoration: "none" }}>{support}</a>
      </footer>

      {buyPlan && <BuyModal plan={buyPlan} stripeEnabled={stripeEnabled} onClose={() => setBuyPlan(null)} />}
    </div>
  );
}
